import { useState } from 'react'
import { Pencil, Activity, Folder, Hash, Type, CheckCircle2, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTeams } from '../hooks'
import type { Team } from '../types'
import { TeamFormDialog } from './TeamFormDialog'

export function GeneralPanel({ team }: { team: Team }) {
  const allTeamsQ = useTeams(true)
  const [editing, setEditing] = useState(false)
  const parent = allTeamsQ.data?.find(t => t.id === team.parentId)

  return (
    <div className="max-w-2xl">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-base">Informacje o zespole</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5">
            <Pencil className="size-3.5" />
            Edytuj
          </Button>
        </CardHeader>
        <CardContent className="grid gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr] text-sm">
          <InfoLabel icon={<Hash className="size-3.5" />}>Kod</InfoLabel>
          <div className="font-mono text-slate-900">{team.code}</div>

          <InfoLabel icon={<Type className="size-3.5" />}>Nazwa</InfoLabel>
          <div className="text-slate-900">{team.name}</div>

          <InfoLabel icon={<Folder className="size-3.5" />}>Rodzic</InfoLabel>
          <div className="text-slate-900">
            {parent ? (
              <>
                {parent.name}{' '}
                <span className="font-mono text-xs text-slate-500">({parent.code})</span>
              </>
            ) : (
              <span className="italic text-slate-500">zespół-korzeń</span>
            )}
          </div>

          <InfoLabel
            icon={team.isActive ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
          >
            Status
          </InfoLabel>
          <div>
            {team.isActive ? (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                Aktywny
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100">
                Nieaktywny
              </Badge>
            )}
          </div>

          <InfoLabel icon={<Activity className="size-3.5" />}>Gotowość pracowników</InfoLabel>
          <div className="flex flex-col gap-1">
            {team.enablesAvailability ? (
              <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 self-start">
                Włączona
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 self-start">
                Wyłączona
              </Badge>
            )}
            <span className="text-[11px] text-slate-500 leading-tight">
              {team.enablesAvailability
                ? 'Członkowie widzą na pasku u góry widget do oznaczania dostępności (Gotowy / Zajęty / Niedostępny). Używane do auto-przypisywania spraw.'
                : 'Pracownicy nie widzą widgetu dostępności. Włącz dla zespołów obsługujących kolejkę spraw (np. Kredyty).'}
            </span>
          </div>
        </CardContent>
      </Card>

      {editing && allTeamsQ.data && (
        <TeamFormDialog
          editing={team}
          allTeams={allTeamsQ.data}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  )
}

function InfoLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500 font-medium pt-1">
      {icon}
      {children}
    </div>
  )
}
